'use client';

import Link from 'next/link';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) return 'Today';
    if (diffDays <= 30) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function CivilSupplierCard({
    supplier,
    categoryMap = {},
    searchParamsStr = '',
    onContactClick,
}) {
    if (!supplier) return null;

    const detailUrl = `/civil/p/${supplier.id}${searchParamsStr ? `?${searchParamsStr}` : ''}`;
    const logo = supplier.supplier_logo || supplier.image;
    const logoUrl = logo
        ? (logo.startsWith('http') || logo.startsWith('data:') ? logo : `${BASE_URL}${logo.startsWith('/') ? logo.slice(1) : logo}`)
        : null;

    // Resolve category names from category_ids JSON array
    let categoryNames = [];
    if (Array.isArray(supplier.category_ids)) {
        categoryNames = supplier.category_ids
            .map(id => categoryMap[id]?.name || categoryMap[id])
            .filter(Boolean);
    }
    if (categoryNames.length === 0 && supplier.services) {
        categoryNames = Array.isArray(supplier.services) ? supplier.services : [supplier.services];
    }

    const phone = supplier.mobile || supplier.phone;

    return (
        <Link
            href={detailUrl}
            className="group bg-white rounded-2xl border border-slate-200/90 hover:border-primary/50 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden cursor-pointer"
        >
            {/* Top Bar with Badges */}
            <div className="bg-gradient-to-r from-slate-50 to-indigo-50/40 p-4 border-b border-slate-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    {supplier.is_verified || supplier.status === 'verified' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs">
                            <svg className="w-3.5 h-3.5 text-emerald-600 fill-current" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                            Supplier
                        </span>
                    )}

                    {supplier.experience && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                            <span>⏱️</span>
                            {supplier.experience.toLowerCase().includes('year') ? supplier.experience : `${supplier.experience} Exp`}
                        </span>
                    )}
                </div>

                {/* Views Count */}
                <div className="flex items-center gap-1 text-[11px] text-slate-400 font-medium shrink-0">
                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>{supplier.views_count || supplier.views || 0} views</span>
                </div>
            </div>

            {/* Supplier Info Section */}
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-start gap-3.5 mb-3">
                    {/* Supplier Logo / Avatar */}
                    <div className="shrink-0 group-hover:opacity-95 transition-opacity">
                        <div className="w-14 h-14 rounded-xl border-2 border-slate-100 shadow-sm overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            {logoUrl ? (
                                <img
                                    src={logoUrl}
                                    alt={supplier.supplier_name || supplier.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        if (e.currentTarget.nextSibling) {
                                            e.currentTarget.nextSibling.style.display = 'flex';
                                        }
                                    }}
                                />
                            ) : null}
                            <div
                                style={{ display: logoUrl ? 'none' : 'flex' }}
                                className="w-full h-full items-center justify-center font-extrabold text-xl text-primary bg-primary/10"
                            >
                                {(supplier.supplier_name || supplier.name || 'S').charAt(0).toUpperCase()}
                            </div>
                        </div>
                    </div>

                    {/* Name & Contact Person */}
                    <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-slate-800 group-hover:text-primary transition-colors leading-snug line-clamp-1">
                            {supplier.supplier_name || supplier.name}
                        </h3>
                        {supplier.contact_name && (
                            <p className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1">
                                <span className="text-slate-400">👤</span>
                                <span>{supplier.contact_name}</span>
                                {supplier.role && (
                                    <span className="text-slate-400 capitalize">({supplier.role})</span>
                                )}
                            </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1 truncate">
                            <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{supplier.address || `${supplier.city || ''}`}</span>
                        </p>
                    </div>
                </div>

                {/* Categories / Products Badges */}
                <div className="mt-1 mb-3 flex flex-wrap gap-1.5 min-h-[30px]">
                    {categoryNames.slice(0, 3).map((catName, idx) => (
                        <span
                            key={idx}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                            📦 {catName}
                        </span>
                    ))}
                    {categoryNames.length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200">
                            +{categoryNames.length - 3} more
                        </span>
                    )}
                </div>

                {/* Service Detail / Description */}
                {(supplier.service_detail || supplier.product_list) && (
                    <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed mb-1 flex-1">
                        {supplier.service_detail || supplier.product_list}
                    </p>
                )}
            </div>
        </Link>
    );
}
